import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Send, AlertTriangle } from "lucide-react";
import WhatIfSimulator from "@/components/WhatIfSimulator";

import { toast } from "sonner";

export default function DecisionsPanel({ decision, hospital, simulation, readOnly, updateDecision, saveDecision, submitDecision }) {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);

  if (!decision) {
    return <Card><CardContent className="p-8 text-center text-gray-400">{t("hospital.noActiveYear") || "No active year"}</CardContent></Card>;
  }

  const isSubmitted = decision.submitted && !readOnly;

  const numField = (field, label, min, max) => (
    <div key={field}><Label className="text-xs">{label}</Label>
      <Input type="number" min={min} max={max} value={decision[field] ?? ""} onChange={e => {
        const raw = e.target.value;
        if (raw === "" || raw === "-") { updateDecision(field, raw); return; }
        const v = parseFloat(raw);
        if (!isNaN(v)) {
          if (v > max) {
            toast.warning(`Maximum allowed for this field is ${max.toLocaleString()}`);
            updateDecision(field, max);
          } else {
            updateDecision(field, v);
          }
        }
      }} onBlur={e => {
        const v = parseFloat(e.target.value);
        if (isNaN(v) || e.target.value === "") updateDecision(field, min);
        else updateDecision(field, Math.max(min, Math.min(max, v)));
      }} data-testid={`decision-${field}`} /></div>
  );

  const selectField = (field, label, options) => {
    const val = decision[field] ?? options[0]?.value ?? "";
    const safeVal = typeof val === "string" ? val : String(val);
    return (
      <div key={field}><Label className="text-xs">{label}</Label>
        <Select value={safeVal} onValueChange={v => updateDecision(field, v)}>
          <SelectTrigger data-testid={`decision-${field}`}><SelectValue /></SelectTrigger>
          <SelectContent>{options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select></div>
    );
  };

  const sliderField = (field, label, min, max, step) => (
    <div key={field}><Label className="text-xs">{label} (${(decision[field] ?? 0).toLocaleString()})</Label>
      <Slider min={min} max={max} step={step} value={[decision[field] ?? 0]} onValueChange={([v]) => updateDecision(field, v)} /></div>
  );

  return (
    <div>
      {isSubmitted && (
        <Card className="mb-4"><CardContent className="p-8 text-center">
          <Badge className="text-sm bg-emerald-100 text-emerald-700 mb-2">Submitted</Badge>
          <p className="text-gray-500">{t("hospital.decisionsSubmitted")}. {t("common.round")} {simulation.current_round}.</p>
        </CardContent></Card>
      )}

      <div className={isSubmitted ? "hidden" : ""}>
        <Tabs defaultValue="staffing">
          <TabsList className="flex-wrap h-auto gap-1">
            {["staffing", "infrastructure", "pricing", "marketing", "operations", "finance", "quality", "strategic"].map(tab => (
              <TabsTrigger key={tab} value={tab} className="text-xs">{t(`hospital.${tab}`)}</TabsTrigger>
            ))}
          </TabsList>

          <div className={readOnly ? "pointer-events-none opacity-80" : ""}>

          <TabsContent value="staffing">
            <Card><CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {numField("doctors_hire", t("hospital.doctors") + " " + t("hospital.hire"), 0, 10)}
                {numField("nurses_hire", t("hospital.nurses") + " " + t("hospital.hire"), 0, 20)}
                {numField("reception_hire", t("hospital.reception") + " " + t("hospital.hire"), 0, 5)}
                {numField("doctors_fire", t("hospital.doctors") + " " + t("hospital.fire"), 0, 10)}
                {numField("nurses_fire", t("hospital.nurses") + " " + t("hospital.fire"), 0, 20)}
                {numField("reception_fire", t("hospital.reception") + " " + t("hospital.fire"), 0, 5)}
              </div>
              {selectField("salary_level", t("hospital.salaryLevel"), [
                { value: "below_market", label: t("hospital.salaryBelow") },
                { value: "market", label: t("hospital.salaryMarket") },
                { value: "above_market", label: t("hospital.salaryAbove") },
                { value: "premium", label: t("hospital.salaryPremium") },
              ])}
              {sliderField("training_budget", t("hospital.training"), 0, 500000, 10000)}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="infrastructure">
            <Card><CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {numField("beds_general_add", t("hospital.bedsGeneral") + " +", 0, 20)}
                {numField("beds_private_add", t("hospital.bedsPrivate") + " +", 0, 10)}
                {numField("beds_icu_add", t("hospital.bedsICU") + " +", 0, 5)}
                {numField("beds_general_remove", t("hospital.bedsGeneral") + " -", 0, 20)}
                {numField("beds_private_remove", t("hospital.bedsPrivate") + " -", 0, 10)}
                {numField("beds_icu_remove", t("hospital.bedsICU") + " -", 0, 5)}
              </div>
              {sliderField("maintenance_budget", t("hospital.maintenance"), 0, 1000000, 10000)}
              {selectField("equipment_investment", t("hospital.equipment"), [
                { value: "basic", label: "Basic ($0)" },
                { value: "standard", label: "Standard ($100K)" },
                { value: "premium", label: "Premium ($300K)" },
                { value: "cutting_edge", label: "Cutting Edge ($600K)" },
              ])}
              {sliderField("renovation_budget", t("hospital.renovation"), 0, 2000000, 50000)}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="pricing">
            <Card><CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {numField("consultation_fee", t("hospital.consultationFee"), 50, 500)}
                {numField("surgery_fee", t("hospital.surgeryFee"), 1000, 20000)}
                {numField("private_room_rate", t("hospital.privateRate"), 200, 2000)}
                {numField("icu_rate", t("hospital.icuRate"), 500, 5000)}
                {numField("general_ward_rate", t("hospital.wardRate"), 50, 800)}
              </div>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="marketing">
            <Card><CardContent className="p-4 space-y-4">
              {sliderField("marketing_budget", t("hospital.marketing"), 0, 1000000, 10000)}
              {selectField("campaign_type", t("hospital.campaign"), [
                { value: "none", label: t("hospital.campaignNone") },
                { value: "awareness", label: t("hospital.campaignAwareness") },
                { value: "premium_brand", label: t("hospital.campaignPremium") },
                { value: "community", label: t("hospital.campaignCommunity") },
                { value: "digital", label: t("hospital.campaignDigital") },
              ])}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="operations">
            <Card><CardContent className="p-4 space-y-4">
              {selectField("admission_policy", t("hospital.admissionPolicy"), [
                { value: "conservative", label: t("hospital.policyConservative") },
                { value: "balanced", label: t("hospital.policyBalanced") },
                { value: "aggressive", label: t("hospital.policyAggressive") },
              ])}
              <div><Label className="text-xs">{t("hospital.emergencyCapacity")} ({decision.emergency_capacity_pct ?? 20}%)</Label>
                <Slider min={5} max={50} step={5} value={[decision.emergency_capacity_pct ?? 20]} onValueChange={([v]) => updateDecision("emergency_capacity_pct", v)} /></div>
              {selectField("target_nurse_ratio", t("hospital.nurseRatioTarget"), [
                { value: "minimal", label: t("hospital.levelMinimal") },
                { value: "standard", label: t("hospital.levelStandard") },
                { value: "premium", label: t("hospital.levelPremium") },
              ])}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="finance">
            <Card><CardContent className="p-4 space-y-4">
              <p className="text-xs text-gray-500 mb-2">{t("hospital.cashLabel")}: ${(hospital.cash ?? 0).toLocaleString()} | {t("hospital.stDebt")}: ${(hospital.short_term_debt ?? 0).toLocaleString()} | {t("hospital.ltDebt")}: ${(hospital.long_term_debt ?? 0).toLocaleString()}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {numField("short_term_borrowing", t("hospital.shortTermBorrowing"), 0, 3000000)}
                {numField("long_term_borrowing", t("hospital.longTermBorrowing"), 0, 10000000)}
                {numField("debt_repayment", t("hospital.debtRepayment"), 0, (hospital.short_term_debt ?? 0) + (hospital.long_term_debt ?? 0))}
              </div>
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="quality">
            <Card><CardContent className="p-4 space-y-4">
              {selectField("quality_program", t("hospital.qualityProgram"), [
                { value: "none", label: t("hospital.qpNone") },
                { value: "basic", label: t("hospital.qpBasic") },
                { value: "standard", label: t("hospital.qpStandard") },
                { value: "comprehensive", label: t("hospital.qpComprehensive") },
              ])}
              {sliderField("safety_investment", t("hospital.safetyInvestment"), 0, 500000, 10000)}
              {selectField("patient_experience", t("hospital.patientExperience"), [
                { value: "none", label: t("hospital.peNone") },
                { value: "basic", label: t("hospital.peBasic") },
                { value: "standard", label: t("hospital.peStandard") },
                { value: "premium", label: t("hospital.pePremium") },
              ])}
            </CardContent></Card>
          </TabsContent>

          <TabsContent value="strategic">
            <Card><CardContent className="p-4 space-y-4">
              <p className="text-xs text-gray-500 mb-2">{t("hospital.strategicHint") || "Elige una iniciativa estrategica por ano (opcional)."}</p>
              {selectField("strategic_decision", t("hospital.strategicDecision"), [
                { value: "none", label: t("hospital.stratNone") },
                { value: "new_department", label: t("hospital.stratNewDept") },
                { value: "tech_upgrade", label: t("hospital.stratTechUpgrade") },
                { value: "partnership", label: t("hospital.stratPartnership") },
                { value: "research_center", label: t("hospital.stratResearch") },
              ])}
            </CardContent></Card>
          </TabsContent>
          </div>
        </Tabs>

        {!readOnly && (
          <div className="flex gap-3 justify-end mt-4">
            <Button variant="outline" onClick={saveDecision} data-testid="save-decisions-btn">{t("common.save")}</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowConfirm(true)} data-testid="submit-decisions-btn">
              <Send className="w-4 h-4 mr-2" />{t("hospital.submitDecisions")}
            </Button>
          </div>
        )}

        {/* Submit Confirmation Dialog */}
        <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                {t("hospital.confirmSubmitTitle") || "Confirm Submission"}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600 dark:text-slate-400 py-2">
              {t("hospital.confirmSubmitMessage") || "Are you sure you want to submit your decisions? Once submitted, you won't be able to modify them unless an instructor unlocks them."}
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="cancel-submit-btn">{t("common.cancel")}</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setShowConfirm(false); submitDecision(); }} data-testid="confirm-submit-btn">
                <Send className="w-4 h-4 mr-2" />{t("hospital.confirmSubmit") || "Yes, Submit"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {simulation.status === "active" && (
          <WhatIfSimulator hospitalId={hospital.id} decision={decision} />
        )}
      </div>
    </div>
  );
}
